// Toont een klein vlak met de credits van het project. Met een muis volgt het
// vlak de cursor zolang je over een tegel hangt, op touch zet een tik hem bij de
// aangetikte tegel. Welke van de twee het is, hangt af van het pointertype van
// het event zelf en niet van het apparaat: een laptop met touchscreen kan beide
export default class Showcase {
	constructor(element) {
		this.offset = 16;
		this.enabled = false;

		this.element = element;
		this.tooltip = element.querySelector('[data-showcase-tooltip]');
		this.projects = Array.from(element.querySelectorAll('[data-showcase-project]'));

		if (!this.tooltip || !this.projects.length) {
			return;
		}

		this.authorLine = this.tooltip.querySelector('.m-showcase__tooltip-author');
		this.authorTarget = this.tooltip.querySelector('[data-showcase-tooltip-author]');
		this.studyTarget = this.tooltip.querySelector('[data-showcase-tooltip-study]');

		this.activeProject = null;
		this.frame = null;

		// Een click vertelt zelf niet of hij van een muis of van een vinger komt,
		// dus dat onthouden we bij de pointerdown ervoor
		this.pointerType = 'mouse';

		this.onPointerDown = this.onPointerDown.bind(this);
		this.onPointerEnter = this.onPointerEnter.bind(this);
		this.onPointerLeave = this.onPointerLeave.bind(this);
		this.onPointerMove = this.onPointerMove.bind(this);
		this.onClick = this.onClick.bind(this);
		this.onDocumentClick = this.onDocumentClick.bind(this);

		this.projects.forEach((project) => {
			project.addEventListener('pointerdown', this.onPointerDown);
			project.addEventListener('pointerenter', this.onPointerEnter);
			project.addEventListener('pointerleave', this.onPointerLeave);
			project.addEventListener('click', this.onClick);
		});

		this.enabled = true;
	}

	onPointerDown(event) {
		this.pointerType = event.pointerType;
	}

	// Een vinger geeft ook een pointerenter, maar die route loopt via de click
	onPointerEnter(event) {
		if (event.pointerType !== 'mouse') {
			return;
		}

		const project = event.currentTarget;

		this.show(project);
		this.positionAtPointer(event);

		project.addEventListener('pointermove', this.onPointerMove);
	}

	onPointerLeave(event) {
		if (event.pointerType !== 'mouse') {
			return;
		}

		event.currentTarget.removeEventListener('pointermove', this.onPointerMove);
		this.hide();
	}

	// Op touch is er geen hover: een tik zet het vlak bij de tegel, nog een tik op
	// dezelfde tegel haalt hem weer weg
	onClick(event) {
		if (this.pointerType === 'mouse') {
			return;
		}

		const project = event.currentTarget;

		if (this.activeProject === project) {
			this.hide();
			return;
		}

		this.show(project);
		this.positionAtProject(project);
	}

	// Een tik naast de tegels sluit het vlak. De tegels zelf gaan langs deze
	// handler heen: die hebben hun eigen click, die ook mag toggelen
	onDocumentClick(event) {
		if (event.target.closest('[data-showcase-project]')) {
			return;
		}

		this.hide();
	}

	show(project) {
		const author = project.dataset.showcaseAuthor || '';
		const study = project.dataset.showcaseStudy || '';

		this.authorTarget.textContent = author;
		this.studyTarget.textContent = study;

		// Een van de twee velden mag leeg zijn, dan valt die regel weg
		this.authorLine.hidden = !author;
		this.studyTarget.hidden = !study;

		// De tegel onder de cursor of vinger blijft vol, de rest fade uit via de SCSS
		this.activeProject?.classList.remove('is-active');
		this.element.classList.add('is-showing-credits');
		project.classList.add('is-active');
		this.tooltip.classList.add('is-visible');

		this.activeProject = project;

		document.addEventListener('click', this.onDocumentClick);
	}

	hide() {
		this.tooltip.classList.remove('is-visible');
		this.element.classList.remove('is-showing-credits');
		this.activeProject?.classList.remove('is-active');
		this.activeProject = null;

		this.cancelFrame();
		document.removeEventListener('click', this.onDocumentClick);
	}

	// De pointermove komt vaker dan een frame, dus alleen de laatste positie
	// per frame wordt gebruikt
	onPointerMove(event) {
		if (this.frame) {
			return;
		}

		this.frame = window.requestAnimationFrame(() => {
			this.frame = null;
			this.positionAtPointer(event);
		});
	}

	positionAtPointer(event) {
		const bounds = this.element.getBoundingClientRect();

		// Beide coördinaten zijn viewport-based, dus de scrollpositie en een
		// transform op een ouder (ScrollSmoother) rekenen automatisch mee
		this.moveTo(event.clientX - bounds.left + this.offset, event.clientY - bounds.top + this.offset, bounds);
	}

	// Het vlak wordt op de aangetikte tegel gecentreerd, met zijn midden op de
	// bovenrand daarvan. Horizontaal centreren in plaats van links uitlijnen,
	// want vanaf md staan er 3 tot 4 tegels op een rij: dan hoort het vlak bij de
	// tegel die je aantikt en niet aan de rand van de module. Het vlak is breder
	// dan een tegel, dus de clamp in moveTo() vangt de buitenste kolommen op
	positionAtProject(project) {
		const bounds = this.element.getBoundingClientRect();
		const projectBounds = project.getBoundingClientRect();

		const x = projectBounds.left - bounds.left + (projectBounds.width - this.tooltip.offsetWidth) / 2;
		const y = projectBounds.top - bounds.top - this.tooltip.offsetHeight / 2;

		this.moveTo(x, y, bounds);
	}

	// Binnen de module blijven, zodat het vlak niet buiten de witte kaart valt
	moveTo(x, y, bounds) {
		const maxX = Math.max(bounds.width - this.tooltip.offsetWidth, 0);
		const maxY = Math.max(bounds.height - this.tooltip.offsetHeight, 0);

		const clampedX = Math.min(Math.max(x, 0), maxX);
		const clampedY = Math.min(Math.max(y, 0), maxY);

		this.tooltip.style.transform = `translate3d(${clampedX}px, ${clampedY}px, 0)`;
	}

	cancelFrame() {
		if (!this.frame) {
			return;
		}

		window.cancelAnimationFrame(this.frame);
		this.frame = null;
	}

	destroy() {
		// De constructor kan afgehaakt zijn (geen tegels met credits), dan is er
		// ook niets om op te ruimen
		if (!this.enabled) {
			return;
		}

		this.activeProject?.removeEventListener('pointermove', this.onPointerMove);
		this.hide();

		this.projects.forEach((project) => {
			project.removeEventListener('pointerdown', this.onPointerDown);
			project.removeEventListener('pointerenter', this.onPointerEnter);
			project.removeEventListener('pointerleave', this.onPointerLeave);
			project.removeEventListener('click', this.onClick);
		});
	}
}
